<template>
  <v-container fluid class="timeline-container">
    <v-row>
      <v-col cols="12" :style="{ 'max-height': maxHeightString }">
        <v-timeline v-if="items" dense reverse>
          <v-timeline-item
            v-for="(item, i) in items"
            :key="i"
            data-qa="bm_timeline_item"
            color="primaryChip"
          >
            <template #icon>
              <slot name="icon" :item="item">
                <v-icon v-if="icon" dark>{{ icon(item) }}</v-icon>
              </slot>
            </template>

            <v-container pa-0>
              <v-row dense>
                <v-col>
                  <bm-card>
                    <slot :item="item"></slot>
                  </bm-card>
                </v-col>
              </v-row>
            </v-container>
          </v-timeline-item>
          <v-timeline-item
            v-if="moreItemsAvailable || items.length === 0"
            small
          >
            <v-container
              v-intersect="{
                handler: loadNextItems,
                options: {
                  threshold: [0, 0.75],
                },
              }"
              pa-0
              @click="loadNextItems"
            >
              <v-row dense>
                <v-col v-if="moreItemsAvailable">
                  <slot name="skeleton">
                    <v-skeleton-loader type="article" />
                  </slot>
                </v-col>
                <v-col v-else-if="items.length === 0">
                  <bm-card>
                    {{ $m.no_data_available }}
                  </bm-card>
                </v-col>
              </v-row>
            </v-container>
          </v-timeline-item>
        </v-timeline>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import BmCard from "@/components/controls/card/BmCard.vue";
import type { PropType } from "vue";
import { computed, defineComponent } from "vue";

//Uses intersection observer. See: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API

/**
 * BmTimeline will fire immediately, meaning that loadNextTimes will be fired on load
 * https://vuetifyjs.com/en/api/v-intersect/#component-pages
 */
export default defineComponent({
  name: "BmTimeline",
  components: { BmCard },

  props: {
    /**
     * All of the items to currently display
     */
    items: {
      type: Array as PropType<any>,
      required: true,
      default: () => [],
    },
    /**
     * Function to produce timeline icon
     */
    icon: {
      type: Function as PropType<(item: any) => string>,
      required: false,
      default: null,
    },
    /**
     * If there are further items to load
     */
    moreItemsAvailable: {
      type: Boolean,
      required: false,
      default: false,
    },
    maxHeight: {
      type: [Number, String],
      required: false,
      default: undefined,
    },
  },

  setup(props, context) {
    const loadNextItems = (entries: IntersectionObserverEntry[]) => {
      // The event can fire on mounted (out of sync with Vue, annoyingly)
      // and sometimes twice, so make sure we are still intersecting when bubbling the event
      if (entries[0]?.isIntersecting && props.moreItemsAvailable) {
        context.emit("loadNextItems", props.items.length);
      }
    };

    const maxHeightString = computed(() =>
      props.maxHeight === undefined || Number.isNaN(props.maxHeight)
        ? props.maxHeight
        : `${props.maxHeight}px`
    );

    return {
      loadNextItems,
      maxHeightString,
    };
  },
});
</script>
<style lang="sass">
.timeline-container
  overflow-y: auto
</style>
